import { useMemo, useEffect, useState, useRef, type KeyboardEvent } from "react"
import { TokenSource, RoomEvent } from "@workspace/livekit/client"
import {
  useSession,
  useSessionContext,
  useAgent,
  useSessionMessages,
} from "@workspace/livekit/react"
import { useInputControls } from "@workspace/livekit/hooks/use-input-controls"
import { useMeridialStore } from "@/store"
import { MeridialLogo } from "@/components/shared/meridial-logo"
import { AgentTrackControl } from "@workspace/livekit/components/agent-track-control"
import { Spinner } from "@workspace/ui/components/spinner"
import { Button } from "@workspace/ui/components/button"
import { AgentSessionProvider } from "@workspace/livekit/components/agent-session-provider"
import { CobrowseLayer } from "@/components/shared/cobrowse/cobrowse-layer"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowUp02Icon,
  CallEnd03Icon,
  ArrowShrink02Icon,
} from "@hugeicons/core-free-icons"
import { StartAudioButton } from "@workspace/livekit/components/start-audio-button"
import { AgentAudioVisualizerAura } from "@workspace/livekit/components/agent-audio-visualizer-aura"
import { cn } from "@workspace/ui/lib/utils"
import { motion, AnimatePresence } from "motion/react"

const AGENT_STATES_READY = ["speaking", "thinking", "listening", "idle"]

interface ChatProps {
  onDisconnect: () => void
  initialInput?: string
  isCollapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
}

export function Chat({
  onDisconnect,
  initialInput,
  isCollapsed,
  onCollapsedChange,
}: ChatProps) {
  const roomNameRef = useRef(crypto.randomUUID())

  const sessionProps = useMeridialStore((s) => s.props)
  const onError =
    sessionProps?.onError ??
    ((error: string) => console.error("[Meridial] ", error))

  const tokenSource = useMemo(
    () =>
      TokenSource.custom(async () => {
        const response = await fetch(
          `${sessionProps?.baseUrl || "https://app.meridial.dev"}/api/auth/livekit/react`,
          {
            method: "POST",
            body: JSON.stringify({
              roomName: roomNameRef.current,
              publishableKey: sessionProps?.publishableKey,
              identifier: sessionProps?.identifier,
              firstMessage: sessionProps?.firstMessage,
              instructions: sessionProps?.instructions,
              metadata: sessionProps?.metadata,
            }),
          }
        )
        if (!response.ok) {
          onError("Oops! Something went wrong. Please try again.")
          return null
        }
        return response.json()
      }),
    [
      sessionProps?.baseUrl,
      sessionProps?.publishableKey,
      sessionProps?.identifier,
      sessionProps?.firstMessage,
      sessionProps?.metadata,
      sessionProps?.instructions,
      onError,
    ]
  )

  const session = useSession(tokenSource)

  useEffect(() => {
    void session.start().catch(() => {
      onError("Oops! Something went wrong. Please try again.")
      onDisconnect()
    })

    // Note: Donot call session.end() in the cleanup function, it disconnects the session when chat states changes.
  }, [])

  const shaderColor = sessionProps?.shaderColor

  return (
    <AgentSessionProvider session={session}>
      <ChatInner
        color={shaderColor}
        onDisconnect={onDisconnect}
        initialInput={initialInput}
        isCollapsed={isCollapsed}
        onCollapsedChange={onCollapsedChange}
      />
      <CobrowseLayer />
    </AgentSessionProvider>
  )
}

function ChatInner({
  onDisconnect,
  initialInput,
  isCollapsed,
  onCollapsedChange,
  color = "#6941c6",
}: {
  onDisconnect: () => void
  initialInput?: string
  isCollapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
  color?: `#${string}`
}) {
  const [input, setInput] = useState(initialInput ?? "")
  const hasSentInitialInputRef = useRef(false)

  const { connectionState, end, room } = useSessionContext()
  const setRoom = useMeridialStore((s) => s.setRoom)
  const { state: agentState } = useAgent()
  const { send, isSending } = useSessionMessages()
  const { microphoneToggle, microphoneTrack } = useInputControls()

  useEffect(() => {
    if (room.state === "connected") setRoom(room)

    const onDisconnected = () => setRoom(null)
    room.on(RoomEvent.Disconnected, onDisconnected)

    return () => {
      room.off(RoomEvent.Disconnected, onDisconnected)
      setRoom(null)
    }
  }, [room, room.state, setRoom])

  const isRoomConnected = connectionState === "connected"
  const isAgentReady = AGENT_STATES_READY.includes(agentState)

  useEffect(() => {
    if (!isRoomConnected || !isAgentReady) return

    const message = initialInput?.trim()
    if (!message || hasSentInitialInputRef.current) return

    hasSentInitialInputRef.current = true
    void send(message).then(() => {
      setInput("")
    })
  }, [isRoomConnected, isAgentReady, initialInput, send])

  async function handleSend() {
    if (input.trim().length === 0) return
    await send(input.trim())
    setInput("")
  }

  function handleInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault()
      handleSend()
    }
  }

  function handleEndConversation() {
    void end()
    onDisconnect()
  }

  function handleExpand() {
    onCollapsedChange(false)
  }

  const statusLabel = !isRoomConnected
    ? "Connecting..."
    : `Agent is ${agentState}`

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col",
        isCollapsed ? "p-2" : "gap-4 p-4"
      )}
    >
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.header
            key="chat-header"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex shrink-0 items-center justify-between"
          >
            <MeridialLogo className="h-10 w-10" />
            <div className="flex items-center gap-2">
              <AgentTrackControl
                source="microphone"
                pressed={microphoneToggle.enabled}
                pending={microphoneToggle.pending}
                disabled={microphoneToggle.pending}
                audioTrack={microphoneTrack}
                onPressedChange={microphoneToggle.toggle}
              />
              <Button
                size="icon"
                variant="ghost"
                aria-label="Collapse chat"
                onClick={() => onCollapsedChange(true)}
              >
                <HugeiconsIcon icon={ArrowShrink02Icon} />
              </Button>
            </div>
          </motion.header>
        )}
      </AnimatePresence>

      <section
        aria-label={isCollapsed ? "Expand chat" : undefined}
        role={isCollapsed ? "button" : undefined}
        tabIndex={isCollapsed ? 0 : undefined}
        onClick={isCollapsed ? handleExpand : undefined}
        onKeyDown={
          isCollapsed
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  handleExpand()
                }
              }
            : undefined
        }
        className={cn(
          "relative flex min-h-0 flex-1 flex-col overflow-hidden",
          isCollapsed && "cursor-pointer"
        )}
      >
        {isCollapsed ? (
          <div className="flex h-full min-h-0 flex-1 items-center gap-2 overflow-hidden">
            <div className="handle shrink-0 cursor-grab">
              {!isRoomConnected ? (
                <Spinner className="size-8" />
              ) : (
                <AgentAudioVisualizerAura
                  size="sm"
                  state={agentState}
                  audioTrack={microphoneTrack}
                  color={color}
                />
              )}
            </div>
            <p className="truncate text-sm -translate-x-[4px]">{statusLabel}</p>
          </div>
        ) : (
          <div className="relative flex h-full min-h-0 flex-1 items-center justify-center">
            {!isRoomConnected ? (
              <Spinner className="size-10" />
            ) : (
              <>
                <p className="absolute top-[50%] -translate-y-1/2 right-1/2 translate-x-1/2 whitespace-nowrap text-sm">
                  {statusLabel}
                </p>
                <AgentAudioVisualizerAura
                  size="xl"
                  state={agentState}
                  audioTrack={microphoneTrack}
                  color={color}
                />
              </>
            )}
          </div>
        )}
      </section>

      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.footer
            key="chat-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex shrink-0 items-center gap-1 cancel-drag"
          >
            <div className="flex w-full items-center rounded-full border border-input bg-background p-1 transition-colors focus-within:border-primary focus-within:ring-3 focus-within:ring-primary/50 dark:bg-input/30">
              <input
                type="text"
                placeholder="Ask me anything"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleInputKeyDown}
                className="w-full px-2.5 focus-visible:outline-none"
              />
              <Button
                size="icon-sm"
                className="rounded-full"
                disabled={input.trim().length === 0 || isSending}
                onClick={handleSend}
              >
                {isSending ? (
                  <Spinner />
                ) : (
                  <HugeiconsIcon icon={ArrowUp02Icon} />
                )}
              </Button>
            </div>
            <Button
              size="icon-lg"
              variant="destructive"
              className="rounded-full"
              onClick={handleEndConversation}
            >
              <HugeiconsIcon icon={CallEnd03Icon} />
            </Button>
            <StartAudioButton label="Start Audio" />
          </motion.footer>
        )}
      </AnimatePresence>
    </div>
  )
}
